import React, { useEffect, useState } from "react";
import Label from "./label";
import useForm from "./index";
import { TForm, TInputParams, TInputProps } from "@/components/form/types";
import PaperclipSVG from "@/assets/icons-js/paperclip";
import useProps from "@/components/form/props";
import useEventListener from "@/hooks/EventListener";
import CrossSVG from "@/assets/icons-js/cross";
import ExternalLinkAltSVG from "@/assets/icons-js/external-link-alt";

const STORE_NAME = "files";
const STALE_EVENT = "stale-filelist";
const DATABASE_NAME = "mekleDB";
const DATABASE_VERSION = 3;

type TFile = {
    content?: string,
    checksum: string,
    lastModified: bigint,
    name: string,
    size: bigint,
    type: string,
}

export const FileInput = (params: TInputParams<TFileInput>) => {
    const handler = useFiles();
    const { container, label, input, tooltip, className, options } = useProps<TFileInput>(params)
    const { value, ...inputRest } = input;
    const [length, setLength] = useState(0)

    useEffect(() => {
        getFiles();
    }, [])

    const getFiles = async () => {
        const files = await handler.all() as Record<string, TDBFile>;
        setLength(Object.keys(files).length)
    }
    useEventListener(STALE_EVENT, getFiles)

    const storeFile = async (db: IDBDatabase, file: File) => {
        const checksum = await handler.checksum(file);
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.index("checksum").get(checksum);

        request.onsuccess = function(event) {
            const result = (event.target as IDBRequest).result as IDBDatabase;
            if (result) {
                console.log("Duplicate file detected:", result);
            } else {
                const writeTransaction = db.transaction([STORE_NAME], "readwrite");
                const writeStore = writeTransaction.objectStore(STORE_NAME);
                const writeRequest = writeStore.add({ checksum: checksum, file: file });

                writeRequest.onsuccess = function() {
                    const staleEvent = new CustomEvent(STALE_EVENT, {
                        bubbles: true,
                    })
                    document.dispatchEvent(staleEvent)
                    console.log("File stored successfully");
                };

                writeRequest.onerror = function(event: Event) {
                    console.log("Error storing file:", event);
                };
            }
        }
    }

    const store = (file: File) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

        // Create an object store if it doesn't already exist
        request.onupgradeneeded = function(event) {
            const db = (event.target as IDBRequest).result as IDBDatabase;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
                objectStore.createIndex("checksum", "checksum", { unique: false });
            }
        };

        request.onerror = function(event) {
            console.log("Error opening IndexedDB:", event);
        };

        request.onsuccess = function(event: Event) {
            const db = (event.target as IDBRequest).result as IDBDatabase;
            storeFile(db, file)
        };
    }

    const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ?? [];
        for (const file of files) {
            store(file);
        }
        e.target.value = "";
    }

    return (
        <div {...container} className={container?.className ?? className}>
            <Label {...label} tooltip={tooltip} options={options}/>
            <div className="form__file">
                <div className="gg-3">
                    <div>
                        { length > 0 ?
                            "Legg til et annet vedlegg" :
                            "Last opp vedlegg"
                        }
                    </div> <PaperclipSVG/>
                </div>
                <input {...inputRest} onChange={onChange} title=""/>
            </div>
        </div>
    )
}

type TDBFile = {
    id: bigint,
    file: File,
    checksum: string,
}
export const FileList = () => {
    const handler = useFiles();
    const [files, setFiles] = useState<TFile[]>([]);

    useEffect(() => {
        getFiles();
    }, [])

    const getFiles = async () => {
        const files = await handler.all() as Record<string, TDBFile>;
        const list = await Promise.all(
            Object.entries(files).map(async ([, item]) => {
                const { content, ...data } = await handler.data(item) as TFile;
                return data;
            })
        );
        setFiles(list);
    }
    useEventListener(STALE_EVENT, getFiles)

    const getFileAsync = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        try {
            return await getFile(e);
        } catch (error) {
            console.warn(error)
            return null;
        }
    }

    const getChecksum = (e: React.SyntheticEvent<HTMLButtonElement>): string => {
        e.preventDefault();
        let parent: HTMLElement | null = e.target as HTMLElement;
        do {
            parent = parent.parentElement;
            if (parent?.getAttribute("data-id")) {
                break;
            }
        } while (parent)
        if (!parent) {
            new Error("Couldn't find the file id.");
            return "";
        }

        return parent.getAttribute("data-id") ?? "";
    }

    const getFile = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
            const checksum = getChecksum(e);

            req.onsuccess = function(event: Event) {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                const transaction = db.transaction([STORE_NAME], "readonly");
                const objectStore = transaction.objectStore(STORE_NAME);
                const request = objectStore.index("checksum").get(checksum);

                request.onsuccess = function (event) {
                    resolve((event.target as IDBRequest).result.file)
                };

                request.onerror = () => {
                    reject(new Error("Found no such file."))
                };
            }

            req.onerror = function() {
                reject(new Error("Could not open database."))
            }
        })
    }

    const destroyFile = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        return new Promise((resolve, reject) => {
            const checksum = getChecksum(e) ?? "";
            const req = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
            console.log(checksum)

            req.onsuccess = function(event: Event) {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                const transaction = db.transaction([STORE_NAME], "readwrite");
                const objectStore = transaction.objectStore(STORE_NAME);
                const request = objectStore.index("checksum").get(checksum);

                request.onsuccess = function (event: Event) {
                    const record = (event.target as IDBRequest).result;
                    if (record) {
                        const deleteRequest = objectStore.delete(record.id);
                        deleteRequest.onsuccess = function() {
                            resolve(true);
                        };

                        deleteRequest.onerror = function() {
                            reject(new Error("Error deleting the file"));
                        };
                    } else {
                        reject(new Error("File not found."));
                    }
                }

                request.onerror = () => {
                    reject(new Error("Found no such file."));
                };
            }

            req.onerror = function() {
                reject(new Error("Could not open database."));
            }
        })
    }

    const view = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        const file = await getFileAsync(e) as File;
        if (!file) return;

        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        document.body.appendChild(a);
        // a.style = "display: none";
        a.target = "_blank";
        a.href = url;
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    const destroy = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        const success = await destroyFile(e) as boolean;
        if (!success) return;

        const staleEvent = new CustomEvent(STALE_EVENT, {
            bubbles: true,
        })
        document.dispatchEvent(staleEvent);
    }

    return (
        <div className="form__files mt-3">
            {files?.map((item: TFile) => {
                return (
                    <div key={item.checksum} data-id={item.checksum}>
                        <div>
                            {item.name}
                            <button onClick={view} style={{ transform: "translate(0, -1px)" }}><ExternalLinkAltSVG/></button>
                            <button onClick={destroy}><CrossSVG/></button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export const useFiles = () => {
    const checksum = async (file: File) => {
        const arrayBuffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    const all = () => {
        return new Promise((resolve, reject) => {
            request((event) => {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                const transaction = db.transaction([STORE_NAME], "readonly");
                const objectStore = transaction.objectStore(STORE_NAME);
                const request = objectStore.getAll();

                request.onsuccess = (event) => {
                    resolve((event.target as IDBRequest).result as IDBDatabase);
                }

                request.onerror = (event: Event) => {
                    const result = (event.target as IDBRequest);
                    const errorMessage = result.error?.message ?? "Unknown error";
                    reject(new Error(`Error getting files: ${errorMessage}`))
                };
            })
        })
    }

    const data = (item: TDBFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function(e) {
                resolve({
                    // @ts-ignore
                    content: e.target?.result?.split(",")[1] ?? null,
                    name: item.file.name,
                    size: item.file.size,
                    type: item.file.type,
                    lastModified: item.file.lastModified,
                    checksum: item.checksum,
                })
            }

            reader.readAsDataURL(item.file);
        })
    }

    const request = (callback: (event: Event) => void) => {
        const req = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

        req.onsuccess = function(event: Event) {
            callback(event);
        }

        req.onupgradeneeded = function(event) {
            const db = (event.target as IDBRequest).result as IDBDatabase;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
                objectStore.createIndex("checksum", "checksum", { unique: false });
            }
        };

        req.onerror = (event: Event) => {
            const result = (event.target as IDBRequest);
            const errorMessage = result.error?.message ?? "Unknown error";
            new Error(`Could not open database: ${errorMessage}`);
        };
    }

    return {
        all,
        data,
        checksum,
        request,
    }
}

type TFileInput = object;
const useFileInput = <T extends TFileInput>(props: TInputProps<T>): TForm<TFileInput> => {
    const form = useForm<TFileInput>({
        id: "string",
        type: "file",
        default: {},
        ...props,
    })

    return {
        ...form,
    }
};

export default useFileInput;
